import type { CourseDetail, Round } from '../api/types';
import { t, type PlainMessageKey } from '../i18n';

/**
 * How the player has actually done on one hole, across their rounds at that course.
 *
 * This is what the canvas's insight card is made of — "Neste buraco você faz média +0,8 —
 * seu terceiro mais difícil". The claim is worthless without its sample, so `rounds` is part
 * of the result and the UI is expected to print it.
 */
export interface HoleStats {
  /** Physical hole number on the course. */
  holeNumber: number;
  par: number;
  /** How many rounds contributed a score for this hole. */
  rounds: number;
  /** Mean strokes over par. Positive is worse. */
  averageOverPar: number;
  best: number;
  worst: number;
  /** null when no round recorded a fairway result (every attempt was a par 3, or unknown). */
  fairwayPct: number | null;
  /** 1 = the hardest hole on the course for this player. */
  difficultyRank: number;
  /** How many holes were ranked, so "3 de 18" can be said honestly. */
  rankedHoles: number;
}

/** Below this the average is noise, and the canvas would rather say nothing. */
export const MIN_ROUNDS_FOR_HOLE_INSIGHT = 3;

/**
 * On a 9-hole course played as 18, logical holes 10–18 replay physical holes 1–9. A hole's
 * history therefore has to gather both passes.
 */
function physicalOf(logicalHole: number, totalHoles: number): number {
  return ((logicalHole - 1) % totalHoles) + 1;
}

interface Tally {
  overPar: number[];
  fairwayAttempts: number;
  fairwayHits: number;
  par: number;
}

function tally(course: CourseDetail, rounds: Round[]): Map<number, Tally> {
  const byHole = new Map<number, Tally>();

  for (const round of rounds) {
    if (round.courseId !== course.id) continue;

    for (const score of round.scores) {
      const physical = physicalOf(score.holeNumber, course.totalHoles);
      const courseHole = course.holes.find((h) => h.number === physical);
      if (!courseHole) continue;

      let entry = byHole.get(physical);
      if (!entry) {
        entry = { overPar: [], fairwayAttempts: 0, fairwayHits: 0, par: courseHole.par };
        byHole.set(physical, entry);
      }

      entry.overPar.push(score.strokes - score.par);
      // fairwayHit is null on par 3 and when it was never recorded; neither is an attempt.
      if (score.fairwayHit !== null) {
        entry.fairwayAttempts++;
        if (score.fairwayHit) entry.fairwayHits++;
      }
    }
  }

  return byHole;
}

const mean = (values: number[]) => values.reduce((a, b) => a + b, 0) / values.length;

/** Stats for every hole the player has a history on, ranked hardest first. */
export function holeStatsFor(course: CourseDetail, rounds: Round[]): Map<number, HoleStats> {
  const tallies = tally(course, rounds);

  const ranked = [...tallies.entries()]
    .filter(([, t]) => t.overPar.length > 0)
    .map(([holeNumber, t]) => ({ holeNumber, t, average: mean(t.overPar) }))
    // Hardest first: the biggest average over par.
    .sort((a, b) => b.average - a.average);

  const result = new Map<number, HoleStats>();
  ranked.forEach(({ holeNumber, t, average }, i) => {
    result.set(holeNumber, {
      holeNumber,
      par: t.par,
      rounds: t.overPar.length,
      averageOverPar: average,
      best: Math.min(...t.overPar) + t.par,
      worst: Math.max(...t.overPar) + t.par,
      fairwayPct: t.fairwayAttempts > 0 ? (t.fairwayHits / t.fairwayAttempts) * 100 : null,
      difficultyRank: i + 1,
      rankedHoles: ranked.length,
    });
  });

  return result;
}

const ORDINALS: Array<PlainMessageKey | null> = [
  null,
  'hole.rank.1',
  'hole.rank.2',
  'hole.rank.3',
  'hole.rank.4',
  'hole.rank.5',
];

/**
 * The rank clause, only when it says something. Being the 14th hardest of 18 is not an
 * insight, so beyond the top five it is omitted rather than padded out.
 */
export function difficultyClause(stats: HoleStats): string | null {
  if (stats.rankedHoles < 6) return null;
  const key = ORDINALS[stats.difficultyRank];
  return key ? t(key) : null;
}
